import { nextTick, onMounted, ref, toValue, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { isFunction, isUndefined } from 'lodash';

import { useNotify } from '@satellite/composables/useNotify';

import type { MaybeRefOrGetter } from 'vue';

type ZendeskApi = ((...args: unknown[]) => void) & { activate?: () => void };

declare global {
  interface Window {
    zE?: ZendeskApi;
  }
}

/**
 * Drives the Zendesk messenger behind the "Support chat" action. On pages with navigation the
 * floating launcher stays hidden and its unread count feeds the nav badge instead.
 * Call from a component `setup`.
 */
export function useZendeskMessenger({ isEnabled }: { isEnabled: MaybeRefOrGetter<boolean> }) {
  const route = useRoute();
  const { t } = useI18n();
  const { notify } = useNotify();

  const unreadMessageCount = ref(0);
  let isSubscribedToMessenger = false;

  // The launcher starts hidden, yet it is the only way to minimize an open chat
  const showLauncher = () => {
    const launcher = document.querySelector<HTMLIFrameElement>('iframe#launcher');
    if (launcher) {
      launcher.style.display = 'block';
    }
  };

  const openSupportChat = () => {
    const zendesk = window.zE;

    if (zendesk?.activate) {
      zendesk.activate();
      // Pages like TV mode hide the messenger, so it must be shown again
      showLauncher();
      nextTick(() => zendesk('messenger', 'show'));
      return;
    }

    if (isFunction(zendesk)) {
      showLauncher();
      nextTick(() => {
        zendesk('messenger', 'show');
        zendesk('messenger', 'open');
      });
      return;
    }

    notify(t('navigation.messages.supportChatUnavailable'), 'error');
  };

  const setupMessenger = () => {
    const zendesk = window.zE;
    if (!isFunction(zendesk)) {
      return;
    }

    if (route.meta.suppressThirdPartyWidgets) {
      zendesk('messenger', 'close');
      zendesk('messenger', 'hide');
      return;
    }

    // Without navigation there is no other way to reach the chat
    if (route.meta.hideSidebar) {
      zendesk('messenger', 'show');
      return;
    }

    zendesk('messenger', 'close');
    zendesk('messenger', 'hide');

    if (!isSubscribedToMessenger) {
      isSubscribedToMessenger = true;
      zendesk('messenger:on', 'unreadMessages', (count: number) => {
        unreadMessageCount.value = count;
      });
      zendesk('messenger:on', 'close', () => zendesk('messenger', 'hide'));
    }
  };

  const bindMessenger = () => {
    if (!toValue(isEnabled)) {
      return;
    }

    if (isUndefined(window.zE)) {
      window.addEventListener('load', setupMessenger, { once: true });
      return;
    }

    setupMessenger();
  };

  onMounted(() => nextTick(bindMessenger));
  watch(() => route.fullPath, bindMessenger);

  return {
    unreadMessageCount,
    openSupportChat
  };
}
